import { ScimSchemaService } from './ScimSchemaService';
import CentralizedSchemaService from './CentralizedSchemaService';
import { getEntityIconName, getEntityColor } from '../utils/navigationIconUtils';
import { debugVerboseLog } from '../utils/debugLog';

export interface EntityCategory {
    type: 'person' | 'locality' | 'other' | 'meta' | 'utilities' | 'blacklist' | 'admin';
    confidence: 'high' | 'medium' | 'low';
    reason: string;
}

export interface EntityInfo {
    entityType: string;
    category: EntityCategory;
    urlSection: 'Person' | 'localities' | 'meta' | 'other' | 'groups' | 'classes' | 'schemas' | 'resourceTypes' | 'admin';
    urlPath: string;
    displayName: string;
    icon: string;
    color: string;
}

export interface ParsedPath {
    section: string;
    entityType: string;
    entityId?: string;
}

export class EntityCategorizationService {
    private static instance: EntityCategorizationService;
    private schemaService: ScimSchemaService;
    private schemaCache: Map<string, any> = new Map();

    // Debug flag - set to true to enable verbose logging
    private static readonly DEBUG_ENTITY_CATEGORIZATION = false;

    private constructor() {
        this.schemaService = new ScimSchemaService();
    }

    public static getInstance(): EntityCategorizationService {
        if (!EntityCategorizationService.instance) {
            EntityCategorizationService.instance = new EntityCategorizationService();
        }
        return EntityCategorizationService.instance;
    }

    /**
     * Categorize multiple entity types in a single batch operation
     */
    public async categorizeEntityTypes(entityTypes: string[]): Promise<Map<string, EntityCategory>> {
        const categories = new Map<string, EntityCategory>();

        try {
            // Fetch schemas once for all entity types using centralized service
            const centralizedService = CentralizedSchemaService.getInstance();
            const allSchemas = await centralizedService.getSchemas();

            // Sort schemas alphabetically by name for consistent navigation ordering
            allSchemas.sort((a: any, b: any) => {
                const nameA = a.name || a.id || '';
                const nameB = b.name || b.id || '';
                return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
            });

            if (EntityCategorizationService.DEBUG_ENTITY_CATEGORIZATION) {
                debugVerboseLog(`🔍 EntityCategorizationService: Batch categorizing ${entityTypes.length} entity types`);
            }

            // Process each entity type
            for (const entityType of entityTypes) {
                try {
                    // Use getEntityCategory to ensure hardcoded rules are applied
                    const category = await this.getEntityCategory(entityType);
                    categories.set(entityType, category);

                    if (EntityCategorizationService.DEBUG_ENTITY_CATEGORIZATION) {
                        debugVerboseLog(`🔍 EntityCategorizationService: Categorized ${entityType} as ${category.type} (${category.confidence} confidence: ${category.reason})`);
                    }
                } catch (error) {
                    console.error(`Error categorizing ${entityType}:`, error);
                    categories.set(entityType, {
                        type: 'other',
                        confidence: 'low',
                        reason: 'Error analyzing schema'
                    });
                }
            }

            return categories;
        } catch (error) {
            console.error('Error in batch categorization:', error);
            // Return default categories for all entity types
            entityTypes.forEach(entityType => {
                categories.set(entityType, {
                    type: 'other',
                    confidence: 'low',
                    reason: 'Error fetching schemas'
                });
            });
            return categories;
        }
    }

    /**
     * Categorize an entity type based on its schema properties (single entity)
     */
    public async categorizeEntityType(entityType: string): Promise<EntityCategory> {
        try {
            // Get the schema for this entity type
            const schema = await this.getSchemaForEntityType(entityType);
            return await this.categorizeFromSchema(schema, entityType);
        } catch (error) {
            console.error(`Error categorizing entity type ${entityType}:`, error);
            return {
                type: 'other',
                confidence: 'low',
                reason: 'Error analyzing schema'
            };
        }
    }

    /**
     * Find schema for entity type from already loaded schemas
     */
    private findSchemaForEntityType(schemas: any[], entityType: string): any {
        const schema = schemas.find((s: any) =>
            s.id === entityType ||
            s.id === `urn:dcps:scim:v1:${entityType}` ||
            s.id === `urn:ietf:params:scim:schemas:core:2.0:${entityType}`
        );

        return schema;
    }

    /**
     * Check if something is actually an entity (not a UI function)
     */
    public isEntity(entityType: string): boolean {
        const lowerEntityType = entityType.toLowerCase();

        // Hardcoded list of non-entities (UI functions)
        const nonEntities = [
            'schoolmap',
            'customfilter',
            'serviceproviderconfig',
            'testing',
            'patchtesting',
            'scimselect',
            'resourcetypes'
        ];

        // Check if it's in our non-entity list
        if (nonEntities.includes(lowerEntityType)) {
            if (EntityCategorizationService.DEBUG_ENTITY_CATEGORIZATION) {
                debugVerboseLog(`🔍 EntityCategorizationService: ${entityType} is a UI function, not an entity`);
            }
            return false;
        }

        // If not in the non-entity list, check if it has a schema
        // This will be used to determine if it's a real entity
        return true;
    }

    /**
     * Check if a schema should be blacklisted (not displayed in navigation)
     */
    private isBlacklisted(schema: any, entityType: string): boolean {
        // Blacklist certain schema types that shouldn't be displayed
        const blacklistedTypes = [
            'Service Provider Configuration',
            'Resource Type',
            'Resource Types',
            'Persons', // Already blacklisted
            'Person', // Blacklist Person since we have hardcoded "All Users" section
            'Group',
            'Groups',
            'schemas' // Deprecated - use schemaentity/schemaattribute instead
        ];

        // Check by entity type name
        if (blacklistedTypes.some(type => entityType.toLowerCase() === type.toLowerCase())) {
            return true;
        }

        // Check by schema ID patterns (only if schema exists)
        if (schema && schema.id) {
            const blacklistedPatterns = [
                'urn:ietf:params:scim:api:messages',
                'urn:ietf:params:scim:schemas:core:2.0:Meta',
                'urn:ietf:params:scim:schemas:extension:enterprise:2.0:User'
            ];

            if (blacklistedPatterns.some(pattern => schema.id.includes(pattern))) {
                return true;
            }
        }

        return false;
    }

    /**
     * Check if an entity is a person based on schema relationships
     */
    private async checkIsPerson(schema: any, entityType: string): Promise<boolean> {
        if (!schema) {
            return false;
        }

        try {
            // Get all schemas to find the Person entity
            const centralizedService = CentralizedSchemaService.getInstance();
            const allSchemas = await centralizedService.getSchemas();

            // Find the Person entity schema (case-insensitive)
            const personSchema = allSchemas.find((s: any) =>
                s.name?.toLowerCase() === 'person' || s.id?.toLowerCase().includes('person')
            );

            if (!personSchema) {
                console.error(`PersonDetection: Person schema not found in ${allSchemas.length} schemas`);
                return false;
            }

            // Check if the Person entity has an Entities property (not attribute)
            const personEntities = (personSchema as any).Entities || [];

            if (!personEntities || personEntities.length === 0) {
                console.error(`Person schema Entities property is missing or empty. Person detection will not work correctly for ${entityType}.`);
                return false;
            }

            // Check if the current entity type is in the Person's entities list (case-insensitive)
            const isPerson = personEntities.some((entityName: string) =>
                entityName.toLowerCase() === entityType.toLowerCase()
            );

            return isPerson;
        } catch (error) {
            console.error(`🔍 PersonDetection: Error checking person status for ${entityType}:`, error);
            return false;
        }
    }

    /**
     * Check if an entity is a locality based on schema attributes
     */
    private checkIsLocality(schema: any): boolean {
        if (!schema || !schema.attributes || !Array.isArray(schema.attributes)) {
            if (EntityCategorizationService.DEBUG_ENTITY_CATEGORIZATION) {
                debugVerboseLog(`🔍 EntityCategorizationService: No schema or attributes for locality check:`, schema);
            }
            return false;
        }

        const properties = schema.attributes.map((attr: any) => attr.name?.toLowerCase() || '').filter(Boolean);

        if (EntityCategorizationService.DEBUG_ENTITY_CATEGORIZATION) {
            debugVerboseLog(`🔍 EntityCategorizationService: Checking locality indicators for entity ${schema.name || 'unknown'} with properties:`, properties);
        }

        // Key locality indicators - if ANY of these are found, it's a locality
        const keyLocalityIndicators = [
            'address', 'city', 'location', 'coordinates'
        ];

        // Check if ANY locality indicator is found (exact match, case-insensitive)
        const foundIndicators = keyLocalityIndicators.filter(indicator =>
            properties.some((prop: string) => prop.toLowerCase() === indicator.toLowerCase())
        );

        const isLocality = foundIndicators.length > 0;

        if (EntityCategorizationService.DEBUG_ENTITY_CATEGORIZATION) {
            debugVerboseLog(`🔍 EntityCategorizationService: Found locality indicators for ${schema.name || 'unknown'}:`, foundIndicators);
            debugVerboseLog(`🔍 EntityCategorizationService: Locality check result: ${isLocality}`);
        }

        return isLocality;
    }

    /**
     * Categorize entity from schema (shared logic)
     */
    private async categorizeFromSchema(schema: any, entityType: string): Promise<EntityCategory> {
        // Check if this entity type should be blacklisted (even without schema)
        if (this.isBlacklisted(schema, entityType)) {
            if (EntityCategorizationService.DEBUG_ENTITY_CATEGORIZATION) {
                debugVerboseLog(`🔍 EntityCategorizationService: Blacklisting ${entityType} (${schema?.id || 'no schema'})`);
            }
            return {
                type: 'blacklist',
                confidence: 'high',
                reason: 'Entity type is blacklisted for display'
            };
        }

        if (!schema) {
            if (EntityCategorizationService.DEBUG_ENTITY_CATEGORIZATION) {
                debugVerboseLog(`🔍 EntityCategorizationService: No schema found for ${entityType}`);
            }
            return {
                type: 'other',
                confidence: 'low',
                reason: 'No schema found'
            };
        }

        // Simple priority-based categorization
        // 1. Check for person indicators first - if ANY found, it's a person
        const isPerson = await this.checkIsPerson(schema, entityType);

        if (isPerson) {
            return {
                type: 'person',
                confidence: 'high',
                reason: 'Person indicators found in schema'
            };
        }

        // 2. Check for locality indicators second - if ANY found, it's a locality
        if (this.checkIsLocality(schema)) {
            if (EntityCategorizationService.DEBUG_ENTITY_CATEGORIZATION) {
                debugVerboseLog(`🔍 EntityCategorizationService: ${entityType} - Locality indicators found`);
            }
            return {
                type: 'locality',
                confidence: 'high',
                reason: 'Locality indicators found in schema'
            };
        }

        // 3. If no clear indicators, it's other
        if (EntityCategorizationService.DEBUG_ENTITY_CATEGORIZATION) {
            debugVerboseLog(`🔍 EntityCategorizationService: ${entityType} - No clear indicators found`);
        }
        return {
            type: 'other',
            confidence: 'low',
            reason: 'No person or locality indicators found'
        };
    }

    /**
     * Get schema for an entity type (with caching) using centralized service
     */
    private async getSchemaForEntityType(entityType: string): Promise<any> {
        if (this.schemaCache.has(entityType)) {
            return this.schemaCache.get(entityType);
        }

        try {
            const centralizedService = CentralizedSchemaService.getInstance();
            const schemas = await centralizedService.getSchemas();

            if (EntityCategorizationService.DEBUG_ENTITY_CATEGORIZATION) {
                debugVerboseLog(`🔍 EntityCategorizationService: Looking for schema for ${entityType}`);
                debugVerboseLog(`🔍 EntityCategorizationService: Available schemas:`, schemas?.map((s: any) => s.id));
            }

            const schema = schemas.find((s: any) =>
                s.id === entityType ||
                s.id === `urn:dcps:scim:v1:${entityType}` ||
                s.id === `urn:ietf:params:scim:schemas:core:2.0:${entityType}`
            );

            if (schema) {
                this.schemaCache.set(entityType, schema);
            } else {
                if (EntityCategorizationService.DEBUG_ENTITY_CATEGORIZATION) {
                    debugVerboseLog(`🔍 EntityCategorizationService: No schema found for ${entityType}`);
                }
            }

            return schema;
        } catch (error) {
            console.error(`Error fetching schema for ${entityType}:`, error);
            return null;
        }
    }





    /**
     * Clear the schema cache
     */
    public clearCache(): void {
        this.schemaCache.clear();
    }

    /**
     * Centralized function to fetch schema for an entity type
     */
    public async fetchSchema(entityType: string): Promise<any> {
        // First check if this is actually an entity (not a UI function)
        if (!this.isEntity(entityType)) {
            if (EntityCategorizationService.DEBUG_ENTITY_CATEGORIZATION) {
                debugVerboseLog(`🔍 EntityCategorizationService: ${entityType} is a UI function, not an entity - skipping schema fetch`);
            }
            return null; // UI functions don't have schemas
        }

        if (this.schemaCache.has(entityType)) {
            return this.schemaCache.get(entityType);
        }

        try {
            const centralizedService = CentralizedSchemaService.getInstance();
            const schemas = await centralizedService.getSchemas();

            if (EntityCategorizationService.DEBUG_ENTITY_CATEGORIZATION) {
                debugVerboseLog(`🔍 EntityCategorizationService: Looking for schema for entity type: ${entityType}`);
            }

            // Try multiple matching strategies for entity type to schema name
            // Since we're now using singular entity types, try exact match first
            let schema = schemas.find((s: any) =>
                s.name?.toLowerCase() === entityType.toLowerCase()
            );

            // If not found, try with common schema ID patterns
            if (!schema) {
                schema = schemas.find((s: any) =>
                    s.id === entityType ||
                    s.id === `urn:dcps:scim:v1:${entityType}` ||
                    s.id === `urn:ietf:params:scim:schemas:core:2.0:${entityType}`
                );
            }

            if (schema) {
                this.schemaCache.set(entityType, schema);
            } else {
                if (EntityCategorizationService.DEBUG_ENTITY_CATEGORIZATION) {
                    console.warn(`⚠️ EntityCategorizationService: No schema found for entity type: ${entityType}`);
                }
            }

            return schema;
        } catch (error) {
            console.error(`Error fetching schema for ${entityType}:`, error);
            return null;
        }
    }

    /**
     * Centralized function to get entity category
     */
    public async getEntityCategory(entityType: string): Promise<EntityCategory> {
        const lowerEntityType = entityType.toLowerCase();

        // First check if this is actually an entity (not a UI function)
        if (!this.isEntity(entityType)) {
            return {
                type: 'blacklist',
                confidence: 'high',
                reason: 'UI function, not an entity'
            };
        }

        // Only hardcode navigation for: "All Users", "Groups", "Classes"
        if (lowerEntityType === 'classes' || lowerEntityType === 'groups') {
            return {
                type: 'other',
                confidence: 'high',
                reason: 'Top-level category that is also an entity type'
            };
        }

        // Hardcode meta entity types
        if (lowerEntityType === 'schemaattribute' || lowerEntityType === 'schemaentity') {
            return {
                type: 'meta',
                confidence: 'high',
                reason: 'Schema-related meta entity type'
            };
        }

        // Hardcode admin entity types
        if (lowerEntityType === 'set' || lowerEntityType === 'policyrule' ||
            lowerEntityType === 'workflow' || lowerEntityType === 'workflowactivity' ||
            lowerEntityType === 'emailtemplate' || lowerEntityType === 'settransition' ||
            lowerEntityType === 'requestapprovaltype' || lowerEntityType === 'functioncatalog' ||
            lowerEntityType === 'journalentry' ||
            lowerEntityType === 'modificationerror') {
            return {
                type: 'admin',
                confidence: 'high',
                reason: 'Admin entity type'
            };
        }

        // Hardcode person subtypes (Users section) so they don't fall into Other when Person.Entities is missing or schema-based check fails
        const personSubtypes = [
            'employee', 'applicant', 'student', 'contractor', 'sponsored', 'shared', 'public', 'vacancy'
        ];
        if (personSubtypes.includes(lowerEntityType)) {
            return {
                type: 'person',
                confidence: 'high',
                reason: 'Known person subtype'
            };
        }

        // Hardcode locality types so Building, School, ResponsibilityCenter, StudentBusStop go under Localities even if schema attributes don't match
        const localityTypes = [
            'building', 'school', 'responsibilitycenter', 'studentbusstop'
        ];
        if (localityTypes.includes(lowerEntityType)) {
            return {
                type: 'locality',
                confidence: 'high',
                reason: 'Known locality type'
            };
        }

        // Get schema for schema-driven categorization
        const schema = await this.fetchSchema(entityType);

        if (EntityCategorizationService.DEBUG_ENTITY_CATEGORIZATION && !schema) {
            console.warn(`⚠️ EntityCategorizationService: No schema found for entity type: ${entityType}`);
        }

        return await this.categorizeFromSchema(schema, entityType);
    }

    // NOTE: We intentionally do NOT have an isPersonEntityType() method here.
    // 
    // Why it was removed - the method was misleading:
    // - It implied checking "is this a person type?" but actual use cases need different checks:
    //   1. For "All Users" view (Type column): check if entityType === 'person', not if it's IN person types
    //   2. For photo display: just check if entity has a Photo property - schema defines this
    //   3. For routing/navigation: use getEntityCategory() which returns the full category
    //
    // If you need the full category, use getEntityCategory().type === 'person'

    /**
     * Centralized function to get relative path for an entity type
     */
    public async getRelativePath(entityType: string): Promise<string> {
        const lowerEntityType = entityType.toLowerCase();

        // First check if this is actually an entity (not a UI function)
        if (!this.isEntity(entityType)) {
            // UI functions should not have relative paths - they're handled specially
            throw new Error(`${entityType} is a UI function, not an entity - cannot generate relative path`);
        }

        // Handle top-level entity types that don't need sub-paths
        if (lowerEntityType === 'classes' || lowerEntityType === 'groups') {
            return lowerEntityType; // Top-level entities go directly to their name
        }

        const category = await this.getEntityCategory(entityType);

        switch (category.type) {
            case 'person':
                return `Person/${lowerEntityType}`;
            case 'locality':
                return `localities/${lowerEntityType}`;
            case 'meta':
                return `meta/${lowerEntityType}`;
            case 'admin':
                return `admin/${lowerEntityType}`; // Admin entities use admin/{entityType} format
            case 'other':
                return `other/${lowerEntityType}`;
            case 'utilities':
                return `utilities/${lowerEntityType}`;
            case 'blacklist':
                return `meta/${lowerEntityType}`; // Blacklisted items go to meta
            default:
                return `other/${lowerEntityType}`;
        }
    }

    /**
     * Centralized function to get entity info from a relative path
     */
    public async getEntityInfoFromRelativePath(path: string): Promise<EntityInfo | null> {
        const parts = path.split('/');
        if (parts.length < 2) {
            return null;
        }

        const section = parts[0];
        const entityType = parts[1];
        const entityId = parts[2]; // Optional entity ID

        // Get entity category
        const category = await this.getEntityCategory(entityType);

        // Determine URL section based on path
        let urlSection: EntityInfo['urlSection'];
        switch (section) {
            case 'Person':
                urlSection = 'Person';
                break;
            case 'localities':
                urlSection = 'localities';
                break;
            case 'meta':
                urlSection = 'meta';
                break;
            case 'admin':
                urlSection = 'admin';
                break;
            case 'other':
                urlSection = 'other';
                break;
            case 'groups':
                urlSection = 'groups';
                break;
            case 'classes':
                urlSection = 'classes';
                break;
            case 'schemas':
                urlSection = 'schemas';
                break;
            case 'resourceTypes':
                urlSection = 'resourceTypes';
                break;
            default:
                urlSection = 'other';
        }

        return {
            entityType,
            category,
            urlSection,
            urlPath: path,
            displayName: this.getDisplayName(entityType),
            icon: getEntityIconName(entityType),
            color: getEntityColor(entityType)
        };
    }

    /**
     * Get display name for an entity type
     */
    private getDisplayName(entityType: string): string {
        // Convert camelCase to Title Case
        return entityType
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    }

}
